import { useEffect, useRef, useState } from 'react';
import { useCrmView } from '@/hooks/useCrmView';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Send,
  MessageSquare,
  CornerDownLeft,
  Paperclip,
  Mic,
  Square,
  X,
} from 'lucide-react';
import * as copilotoApi from '@/api/copiloto.api';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { enlaceAPantalla } from '@/lib/mapa-sistema';
import { cn } from '@/lib/cn';
import { useGuia } from '@/components/guia/guia-contexto';

/**
 * LA PANTALLA DEL ASISTENTE.
 *
 * La ventanita flotante sirve para una pregunta suelta desde donde uno esté.
 * Esto es lo otro: sentarse a trabajar con él. Historial a la izquierda,
 * búsqueda, y espacio para leer una respuesta larga sin hacer scroll en una
 * cajita de 22rem.
 *
 * DOS COSAS QUE NO SON OBVIAS Y CONVIENE SABER:
 *
 *  - **Abrir un hilo viejo NO lo vuelve el activo.** Mirar no es retomar. El
 *    activo es el que usa WhatsApp, y cambiarlo por haber mirado algo dejaría
 *    los mensajes del teléfono cayendo en una conversación de la semana pasada.
 *    Por eso un hilo viejo se abre en solo lectura, y para seguir hablando ahí
 *    hay un botón que lo dice.
 *  - **La búsqueda mira dentro de los mensajes**, no solo en los títulos: lo
 *    que uno recuerda es una frase que dijo.
 */

interface Burbuja {
  quien: 'yo' | 'copiloto';
  texto: string;
  botones?: copilotoApi.BotonCopiloto[];
}

/**
 * Lo que la gente pregunta todos los días. Enseña qué se le puede pedir — y por
 * eso no puede traer preguntas de otro negocio: un atajo que ofrece "¿qué
 * garantías hay abiertas?" en una tienda de ropa enseña algo que no existe, y la
 * respuesta va a ser "ninguna" para siempre.
 */
const ATAJOS_COMUNES = [
  'Haz una auditoría del día',
  '¿Quién está esperando respuesta?',
  '¿Qué le prometimos a los clientes?',
  '¿Cómo va el día?',
];
const ATAJOS_INSTALACIONES = [
  '¿Quién está trabajando hoy?',
  '¿Hay clientes quejándose?',
  '¿Qué garantías hay abiertas?',
];
const ATAJOS_TIENDA = ['¿A quién le escribimos y no contestó?', '¿Cómo quedó el reparto de hoy?'];

export function Asistente() {
  // Los atajos de este negocio: los comunes + los de su rubro.
  const usaInstalaciones = useCrmView()?.osito?.modulos?.instalaciones !== false;
  const ATAJOS = [
    ...ATAJOS_COMUNES,
    ...(usaInstalaciones ? ATAJOS_INSTALACIONES : ATAJOS_TIENDA),
  ];

  const [borrador, setBorrador] = useState('');
  const [burbujas, setBurbujas] = useState<Burbuja[]>([]);
  const [hiloAbierto, setHiloAbierto] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [configurando, setConfigurando] = useState(false);
  // Una foto esperando a que se mande junto con el texto. Se manda con el
  // mensaje, no sola: una imagen sin decir qué hacer con ella no es un pedido.
  const [foto, setFoto] = useState<{
    clave: string;
    dataBase64: string;
    mediaType: string;
    url: string;
  } | null>(null);
  const [grabando, setGrabando] = useState(false);
  const [transcribiendo, setTranscribiendo] = useState(false);
  const grabadora = useRef<MediaRecorder | null>(null);
  const archivoRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const guia = useGuia();
  const qc = useQueryClient();
  const finDelHilo = useRef<HTMLDivElement>(null);

  const hilos = useQuery({
    queryKey: ['copiloto', 'hilos'],
    queryFn: copilotoApi.listarHilos,
  });

  const activo = hilos.data?.hilos.find((h) => h.activo) ?? null;
  // Solo se puede escribir en el activo. Ver la nota de arriba.
  const esSoloLectura = hiloAbierto !== null && hiloAbierto !== activo?.id;

  const hiloActual = useQuery({
    queryKey: ['copiloto', 'hilo', hiloAbierto ?? activo?.id ?? 'ninguno'],
    queryFn: () => copilotoApi.traerHilo2(hiloAbierto ?? activo!.id),
    enabled: Boolean(hiloAbierto ?? activo?.id),
  });

  const resultados = useQuery({
    queryKey: ['copiloto', 'buscar', busqueda],
    queryFn: () => copilotoApi.buscarEnLosHilos(busqueda),
    enabled: busqueda.trim().length >= 3,
  });

  useEffect(() => {
    if (!hiloActual.data) return;
    setBurbujas(
      hiloActual.data.mensajes.map((m) => ({
        quien: m.role === 'assistant' ? ('copiloto' as const) : ('yo' as const),
        texto: m.content,
      })),
    );
  }, [hiloActual.data]);

  useEffect(() => {
    finDelHilo.current?.scrollIntoView({ behavior: 'smooth' });
  }, [burbujas]);

  const mandar = useMutation({
    mutationFn: ({ texto, imagen }: { texto: string; imagen?: typeof foto }) =>
      copilotoApi.mandarMensajeConFoto(
        texto,
        imagen
          ? { clave: imagen.clave, dataBase64: imagen.dataBase64, mediaType: imagen.mediaType }
          : undefined,
      ),
    onSuccess: (r) => {
      setConfigurando(Boolean(r.puestaEnMarcha));
      setBurbujas((prev) => [...prev, { quien: 'copiloto', texto: r.texto, botones: r.botones }]);
      void qc.invalidateQueries({ queryKey: ['copiloto', 'hilos'] });
    },
    onError: () =>
      // Nunca se queda mudo: una pantalla que no contesta se lee como rota.
      setBurbujas((prev) => [
        ...prev,
        { quien: 'copiloto', texto: 'No pude contestarte ahora. Prueba de nuevo.' },
      ]),
  });

  const nuevo = useMutation({
    mutationFn: copilotoApi.abrirHiloNuevo,
    onSuccess: () => {
      setBurbujas([]);
      setHiloAbierto(null);
      setBusqueda('');
      void qc.invalidateQueries({ queryKey: ['copiloto', 'hilos'] });
    },
  });

  function mandarTexto(texto: string) {
    const t = texto.trim();
    if (!t || mandar.isPending) return;
    // Escribir en un hilo viejo lo retoma: se vuelve el activo.
    if (esSoloLectura) setHiloAbierto(null);
    setBurbujas((prev) => [...prev, { quien: 'yo', texto: t }]);
    setBorrador('');
    mandar.mutate({ texto: t, imagen: foto ?? undefined });
    // La foto se consume con el mensaje: mandarla dos veces sin querer sería
    // subirla dos veces al catálogo.
    setFoto(null);
  }

  /** Sube una foto y la deja esperando al texto. */
  async function elegirFoto(archivo: File) {
    try {
      const r = await copilotoApi.subirAdjunto(archivo);
      if (r.tipo === 'imagen') {
        setFoto({ clave: r.clave, dataBase64: r.dataBase64, mediaType: r.mediaType, url: r.url });
      }
    } catch {
      setBurbujas((prev) => [
        ...prev,
        { quien: 'copiloto', texto: 'No pude subir esa foto. Prueba con otra.' },
      ]);
    }
  }

  /**
   * Graba una nota de voz y la deja TRANSCRIPTA en el cuadro de texto.
   *
   * No se manda sola a propósito: quien dicta quiere leer lo que entendió antes
   * de mandarlo, sobre todo si va a disparar una acción.
   */
  async function alternarGrabacion() {
    if (grabando) {
      grabadora.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const trozos: BlobPart[] = [];
      rec.ondataavailable = (e) => trozos.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setGrabando(false);
        setTranscribiendo(true);
        try {
          const blob = new Blob(trozos, { type: rec.mimeType || 'audio/webm' });
          const r = await copilotoApi.subirAdjunto(
            new File([blob], 'nota.webm', { type: blob.type }),
          );
          if (r.tipo === 'audio' && r.texto) {
            // Se agrega a lo que ya haya escrito, no lo reemplaza.
            setBorrador((prev) => (prev ? `${prev} ${r.texto}` : r.texto!));
          } else if (r.tipo === 'audio') {
            setBurbujas((prev) => [
              ...prev,
              { quien: 'copiloto', texto: r.error ?? 'No pude entender el audio.' },
            ]);
          }
        } catch {
          setBurbujas((prev) => [
            ...prev,
            { quien: 'copiloto', texto: 'No pude procesar el audio. Escríbeme mejor.' },
          ]);
        } finally {
          setTranscribiendo(false);
        }
      };
      grabadora.current = rec;
      rec.start();
      setGrabando(true);
    } catch {
      // Permiso denegado o sin micrófono. Se dice, no se queda el botón mudo.
      setBurbujas((prev) => [
        ...prev,
        {
          quien: 'copiloto',
          texto: 'No pude usar el micrófono. Revisa el permiso del navegador, o escríbeme.',
        },
      ]);
    }
  }

  return (
    <div className="flex h-full min-h-0">
      {/* ───────────────────────────── historial */}
      <aside className="hidden w-72 shrink-0 flex-col border-r border-surface-border bg-white md:flex dark:border-slate-800 dark:bg-slate-900">
        <div className="space-y-2 border-b border-surface-border p-3 dark:border-slate-800">
          <Button className="w-full" onClick={() => nuevo.mutate()} loading={nuevo.isPending}>
            <Plus className="mr-1 h-4 w-4" /> Conversación nueva
          </Button>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar en lo que hablamos…"
              aria-label="Buscar en las conversaciones"
              className="w-full rounded-md border border-surface-border bg-transparent py-1.5 pl-8 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-slate-700"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {/* Con búsqueda activa, la lista muestra los resultados. */}
          {busqueda.trim().length >= 3 ? (
            <>
              {resultados.isLoading && <Spinner />}
              {resultados.data?.resultados.length === 0 && (
                <p className="px-2 py-3 text-sm text-slate-500">
                  No encontré nada con esas palabras.
                </p>
              )}
              {resultados.data?.resultados.map((r, i) => (
                <button
                  key={`${r.hiloId}-${i}`}
                  type="button"
                  onClick={() => {
                    setHiloAbierto(r.hiloId);
                    setBusqueda('');
                  }}
                  className="mb-1 w-full rounded-md px-2 py-2 text-left transition hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">
                    {r.titulo}
                  </p>
                  <p className="line-clamp-2 text-xs text-slate-500">{r.fragmento}</p>
                </button>
              ))}
            </>
          ) : (
            <>
              {hilos.isLoading && <Spinner />}
              {hilos.data?.hilos.length === 0 && (
                <p className="px-2 py-3 text-sm text-slate-500">
                  Todavía no hablaste conmigo. Pregúntame algo.
                </p>
              )}
              {hilos.data?.hilos.map((h) => (
                <button
                  key={h.id}
                  type="button"
                  onClick={() => setHiloAbierto(h.activo ? null : h.id)}
                  className={cn(
                    'mb-1 flex w-full items-start gap-2 rounded-md px-2 py-2 text-left transition',
                    (hiloAbierto ?? activo?.id) === h.id
                      ? 'bg-brand-50 dark:bg-slate-800'
                      : 'hover:bg-slate-100 dark:hover:bg-slate-800',
                  )}
                >
                  <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-slate-700 dark:text-slate-200">
                      {h.titulo}
                    </span>
                    {h.activo && (
                      <span className="text-[11px] text-brand-600 dark:text-brand-400">
                        En curso · es la del WhatsApp
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </>
          )}
        </div>
      </aside>

      {/* ───────────────────────────── la conversación */}
      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-surface-border px-4 py-3 dark:border-slate-800">
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold text-slate-800 dark:text-slate-100">
              {hiloAbierto
                ? (hilos.data?.hilos.find((h) => h.id === hiloAbierto)?.titulo ?? 'Conversación')
                : 'Asistente'}
            </h1>
            <p className="text-xs text-slate-500">
              {esSoloLectura
                ? 'Estás mirando una conversación anterior'
                : 'Pregúntame lo que quieras del negocio'}
            </p>
          </div>
          <Button variant="outline" size="sm" className="md:hidden" onClick={() => nuevo.mutate()}>
            <Plus className="h-4 w-4" />
          </Button>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {hiloActual.isLoading && <Spinner />}

          {!hiloActual.isLoading && burbujas.length === 0 && (
            <div className="mx-auto max-w-lg px-2 py-8 text-center">
              <p className="text-sm text-slate-500">
                Pregúntame por el día, por un cliente, por el equipo. Si lo que buscas está en una
                pantalla, te dejo el botón para ir directo.
              </p>
            </div>
          )}

          {burbujas.map((b, i) => (
            <div key={i} className={cn('flex', b.quien === 'yo' ? 'justify-end' : 'justify-start')}>
              <div
                className={cn(
                  'max-w-[min(46rem,85%)] rounded-lg px-3.5 py-2.5 text-sm',
                  b.quien === 'yo'
                    ? 'bg-brand-600 text-white'
                    : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
                )}
              >
                <p className="whitespace-pre-wrap">{b.texto}</p>

                {(b.botones ?? []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(b.botones ?? []).map((boton, j) => {
                      const destino = enlaceAPantalla(boton.ruta, boton.filtros);
                      // Ruta que ya no existe: NO se dibuja.
                      if (!destino) return null;
                      // "Muéstrame" que este rol no puede usar: tampoco.
                      if (boton.receta && !guia.puedeUsar(boton.receta)) return null;
                      return (
                        <button
                          key={j}
                          type="button"
                          onClick={() =>
                            boton.receta ? guia.iniciar(boton.receta) : navigate(destino)
                          }
                          className="rounded-full border border-brand-500 px-2.5 py-1 text-xs font-medium text-brand-700 transition hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-slate-700"
                        >
                          {boton.receta ? `Muéstrame: ${boton.etiqueta}` : boton.etiqueta}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}

          {mandar.isPending && (
            <div className="flex justify-start">
              <div className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-500 dark:bg-slate-800">
                Pensando…
              </div>
            </div>
          )}

          <div ref={finDelHilo} />
        </div>

        {configurando && (
          <p className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
            Todavía me estás poniendo en marcha. Hasta que terminemos no puedo ver los datos del
            negocio.
          </p>
        )}

        {esSoloLectura ? (
          <div className="flex items-center justify-between gap-3 border-t border-surface-border px-4 py-3 dark:border-slate-800">
            <p className="text-xs text-slate-500">
              Esta conversación ya terminó. Si escribes, la retomo y pasa a ser la del WhatsApp.
            </p>
            <Button variant="outline" size="sm" onClick={() => setHiloAbierto(null)}>
              <CornerDownLeft className="mr-1 h-4 w-4" /> Volver a la actual
            </Button>
          </div>
        ) : (
          <>
            {!mandar.isPending && burbujas.length === 0 && (
              <div className="flex flex-wrap gap-1.5 border-t border-surface-border px-4 pt-2.5 dark:border-slate-800">
                {ATAJOS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => mandarTexto(a)}
                    className="rounded-full border border-surface-border px-2.5 py-1 text-xs text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    {a}
                  </button>
                ))}
              </div>
            )}

            {foto && (
              <div className="flex items-center gap-3 border-t border-surface-border px-3 pt-2.5 dark:border-slate-800">
                <img
                  src={foto.url}
                  alt="La foto que vas a mandar"
                  className="h-14 w-14 rounded object-cover"
                />
                <p className="flex-1 text-xs text-slate-500">
                  Va con tu próximo mensaje. Dime qué hago con ella — por ejemplo, a qué modelo del
                  catálogo la subo.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Quitar la foto"
                  onClick={() => setFoto(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            <div className="flex items-end gap-2 border-t border-surface-border p-3 dark:border-slate-800">
              <input
                ref={archivoRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void elegirFoto(f);
                  e.target.value = '';
                }}
              />
              <Button
                variant="ghost"
                size="sm"
                aria-label="Adjuntar una foto"
                title="Adjuntar una foto"
                onClick={() => archivoRef.current?.click()}
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <Button
                variant={grabando ? 'danger' : 'ghost'}
                size="sm"
                aria-label={grabando ? 'Parar de grabar' : 'Grabar una nota de voz'}
                title={grabando ? 'Parar de grabar' : 'Grabar una nota de voz'}
                loading={transcribiendo}
                onClick={() => void alternarGrabacion()}
              >
                {grabando ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
              <textarea
                value={borrador}
                rows={1}
                onChange={(e) => setBorrador(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    mandarTexto(borrador);
                  }
                }}
                placeholder={
                  grabando ? 'Grabando… toca el cuadrado para parar' : 'Escríbele al asistente…'
                }
                aria-label="Escribile al asistente"
                className="max-h-40 min-h-[2.5rem] min-w-0 flex-1 resize-y rounded-md border border-surface-border bg-transparent px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-slate-700"
              />
              <Button
                aria-label="Enviar"
                onClick={() => mandarTexto(borrador)}
                loading={mandar.isPending}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
